import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target, Plus, X, Loader2, ShieldAlert, ExternalLink, Clock, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  fetchPositions,
  openPosition,
  closePosition,
} from "../lib/api";

// Poll backend every 10s — same cadence as the position_tracker tick so the
// UI is at most one tick stale.
const REFRESH_MS = 10_000;

// Solana mint Base58 shape (matches what watchlist accepts).
const SOL_MINT_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

const TRIGGER_META = {
  exit_backstop_25:        { emoji: "🛑", short: "Backstop −25%", tone: "danger"  },
  exit_t15_hard:           { emoji: "⏱️", short: "T+15 hard",     tone: "danger"  },
  exit_t60_recheck:        { emoji: "🧭", short: "T+60 recheck",  tone: "warning" },
  exit_t5_flat:            { emoji: "⏰", short: "T+5 flat",      tone: "warning" },
  exit_t10_green_move_stop:{ emoji: "🟢", short: "T+10 green",    tone: "success" },
};

function shortMint(m) {
  if (!m) return "—";
  return `${m.slice(0, 4)}…${m.slice(-4)}`;
}

function fmtUsd(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1) return `$${Number(n).toFixed(4)}`;
  return `$${Number(n).toPrecision(3)}`;
}

function fmtPct(n) {
  if (n == null || isNaN(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${Number(n).toFixed(2)}%`;
}

function fmtElapsed(sec) {
  if (sec == null || isNaN(sec)) return "—";
  const s = Math.max(0, Math.floor(sec));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}


// ---------------------------------------------------------------------------
// Open-position modal
// ---------------------------------------------------------------------------

function OpenPositionModal({ open, onClose, onCreated }) {
  const [mint, setMint] = React.useState("");
  const [entryPrice, setEntryPrice] = React.useState("");
  const [sizeSol, setSizeSol] = React.useState("0.5");
  const [feesPct, setFeesPct] = React.useState("1.5");
  const [thesis, setThesis] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      setMint(""); setEntryPrice(""); setSizeSol("0.5");
      setFeesPct("1.5"); setThesis(""); setErr(null);
    }
  }, [open]);

  if (!open) return null;

  const mintTrim = mint.trim();
  const validMint = SOL_MINT_RE.test(mintTrim);
  const validEntry = Number(entryPrice) > 0;
  const validSize = Number(sizeSol) > 0;
  const validFees = Number(feesPct) >= 0 && Number(feesPct) <= 20;
  const formValid = validMint && validEntry && validSize && validFees && !submitting;

  const submit = async (e) => {
    e?.preventDefault();
    if (!formValid) return;
    setSubmitting(true);
    setErr(null);
    try {
      const { position } = await openPosition({
        mintAddress: mintTrim,
        entryPrice: Number(entryPrice),
        sizeSol: Number(sizeSol),
        feesBufferPct: Number(feesPct),
        thesis: thesis.trim() || null,
      });
      toast.success(`Tracking ${position.ticker || shortMint(position.mint)} — ${fmtUsd(position.entry_price)}`);
      onCreated?.(position);
      onClose();
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      // 409 conflict: {ok: false, reason: "reentry_guard" | "cap_24h", ...}
      if (detail && typeof detail === "object" && detail.reason) {
        if (detail.reason === "reentry_guard") {
          const until = detail.until_ts ? new Date(detail.until_ts * 1000).toLocaleTimeString() : "soon";
          setErr(`Re-entry guard active until ${until}. Wait it out or clear it on the panel.`);
        } else if (detail.reason === "cap_24h") {
          setErr(`24h cap reached (${detail.count} positions on this mint). Try again tomorrow.`);
        } else {
          setErr(`Blocked: ${detail.reason}`);
        }
      } else if (typeof detail === "string") {
        setErr(detail);
      } else {
        setErr(e2?.message || "Failed to open position");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Mark as in position"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="relative w-full max-w-md bg-[hsl(var(--bg-elev-1))] border border-[hsl(var(--border))] rounded-lg shadow-xl"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--border))]">
          <div className="flex items-center gap-2">
            <Target size={15} className="text-[hsl(var(--muted))]" />
            <h3 className="text-sm font-semibold">Mark as in position</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-[hsl(var(--border))] hover:bg-[hsl(var(--bg-elev-3))]"
          >
            <X size={14} />
          </button>
        </div>

        <form onSubmit={submit} className="px-4 py-3 space-y-3" data-testid="open-position-form">
          <div>
            <label className="text-[11px] text-[hsl(var(--muted))] block mb-1">Mint address</label>
            <Input
              value={mint}
              onChange={(e) => setMint(e.target.value)}
              placeholder="So11111111111111111111111111111111111111112"
              data-testid="open-position-mint"
              className="font-mono text-xs"
              autoFocus
            />
            {mintTrim && !validMint ? (
              <p className="text-[10px] text-[hsl(var(--danger))] mt-1">Not a valid Solana mint address.</p>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-[hsl(var(--muted))] block mb-1">Entry price (USD)</label>
              <Input
                type="number"
                step="any"
                value={entryPrice}
                onChange={(e) => setEntryPrice(e.target.value)}
                placeholder="0.0042"
                data-testid="open-position-entry"
              />
            </div>
            <div>
              <label className="text-[11px] text-[hsl(var(--muted))] block mb-1">Size (SOL)</label>
              <Input
                type="number"
                step="any"
                value={sizeSol}
                onChange={(e) => setSizeSol(e.target.value)}
                placeholder="0.5"
                data-testid="open-position-size"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-[hsl(var(--muted))] block mb-1">
              Fees + slippage buffer (%) <span className="text-[hsl(var(--muted))]">— round-trip estimate</span>
            </label>
            <Input
              type="number"
              step="0.1"
              value={feesPct}
              onChange={(e) => setFeesPct(e.target.value)}
              placeholder="1.5"
              data-testid="open-position-fees"
            />
            <p className="text-[10px] text-[hsl(var(--muted))] mt-1">
              P&amp;L for exit triggers is computed net of this buffer.
            </p>
          </div>
          <div>
            <label className="text-[11px] text-[hsl(var(--muted))] block mb-1">Thesis (optional)</label>
            <Input
              value={thesis}
              onChange={(e) => setThesis(e.target.value)}
              placeholder="e.g. fib_618 bounce, 50k liq, deployer clean"
              data-testid="open-position-thesis"
            />
          </div>
          {err ? (
            <div className="text-xs text-[hsl(var(--danger))] bg-[hsl(var(--danger)/0.08)] border border-[hsl(var(--danger)/0.3)] rounded px-2 py-1.5">
              {err}
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              size="sm"
              disabled={!formValid}
              data-testid="open-position-submit"
            >
              {submitting ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
              Track position
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Single position row
// ---------------------------------------------------------------------------

function PositionRow({ pos, onClosed }) {
  const [closing, setClosing] = React.useState(false);

  const handleClose = async () => {
    if (closing) return;
    if (!window.confirm(`Close ${pos.ticker || shortMint(pos.mint)}? This will arm the 4h re-entry guard.`)) {
      return;
    }
    setClosing(true);
    try {
      const { position } = await closePosition(pos.id, { armReentryGuard: true });
      const net = position.pnl_pct_net;
      toast.success(`Closed ${position.ticker || shortMint(position.mint)} · ${fmtPct(net)} net`);
      onClosed?.(position);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Close failed");
    } finally {
      setClosing(false);
    }
  };

  const fired = Array.isArray(pos.triggers_fired) ? pos.triggers_fired : [];
  const lastTrigger = fired[fired.length - 1];
  const lastMeta = lastTrigger ? TRIGGER_META[lastTrigger] : null;
  const elapsed = (Date.now() / 1000) - Number(pos.entry_ts || 0);

  return (
    <div
      className="px-3 py-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
      data-testid={`position-row-${pos.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Target size={13} className="text-[hsl(var(--muted))]" />
          <span className="font-medium text-sm truncate">
            {pos.ticker || shortMint(pos.mint)}
          </span>
          <span className="text-[10px] font-mono text-[hsl(var(--muted))]">
            {shortMint(pos.mint)}
          </span>
          {lastMeta ? (
            <Badge variant={lastMeta.tone} className="h-4 px-1 text-[9px]">
              {lastMeta.emoji} {lastMeta.short}
            </Badge>
          ) : null}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleClose}
          disabled={closing}
          data-testid={`position-close-${pos.id}`}
        >
          {closing ? <Loader2 size={11} className="animate-spin" /> : "Close"}
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-2 text-[11px]">
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[hsl(var(--muted))]">Entry</div>
          <div className="font-mono">{fmtUsd(pos.entry_price)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[hsl(var(--muted))]">Size</div>
          <div className="font-mono">{Number(pos.size_sol).toFixed(3)} SOL</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[hsl(var(--muted))]">Fees buf</div>
          <div className="font-mono">{Number(pos.fees_buffer_pct).toFixed(1)}%</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[hsl(var(--muted))]">Elapsed</div>
          <div className="font-mono">{fmtElapsed(elapsed)}</div>
        </div>
      </div>
      {pos.thesis ? (
        <div className="text-[10px] text-[hsl(var(--muted))] mt-1.5 italic truncate" title={pos.thesis}>
          {pos.thesis}
        </div>
      ) : null}
      {fired.length > 0 ? (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {fired.map((t) => {
            const m = TRIGGER_META[t];
            if (!m) return null;
            return (
              <span
                key={t}
                className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-1))]"
                title={`Trigger fired: ${m.short}`}
              >
                {m.emoji}<span className="text-[hsl(var(--muted))]">{m.short}</span>
              </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Recent-closed row (includes re-entry guard badge if still active)
// ---------------------------------------------------------------------------

function ClosedPositionRow({ pos, isGuarded }) {
  const net = pos.pnl_pct_net;
  const profitable = net != null && net > 0;
  return (
    <div
      className="px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
      data-testid={`position-closed-${pos.id}`}
    >
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium truncate">
            {pos.ticker || shortMint(pos.mint)}
          </span>
          <span className="text-[10px] font-mono text-[hsl(var(--muted))]">
            {shortMint(pos.mint)}
          </span>
          {isGuarded ? (
            <Badge variant="warning" className="h-4 px-1 text-[9px]" data-testid={`reentry-guard-${pos.mint}`}>
              <ShieldAlert size={9} className="mr-0.5" /> guard
            </Badge>
          ) : null}
        </div>
        <span
          className={`font-mono text-[11px] ${
            profitable
              ? "text-[hsl(var(--success))]"
              : "text-[hsl(var(--danger))]"
          }`}
        >
          {fmtPct(net)}
        </span>
      </div>
      <div className="flex items-center justify-between text-[10px] text-[hsl(var(--muted))] font-mono mt-1">
        <span>{fmtUsd(pos.entry_price)} → {fmtUsd(pos.exit_price)}</span>
        <span>{pos.exit_reason || "—"}</span>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Top-level panel
// ---------------------------------------------------------------------------

export function PositionsPanel() {
  const [open, setOpen] = React.useState([]);
  const [closed, setClosed] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);

  // Mints currently inside the re-entry guard window. Computed client-side
  // from the close timestamps + the 4h default — purely cosmetic; the
  // server enforces the real guard.
  const guardedMints = React.useMemo(() => {
    const now = Date.now() / 1000;
    const GUARD_SEC = 4 * 3600;
    const set = new Set();
    for (const p of closed) {
      if (!p.exit_ts) continue;
      if ((p.exit_ts + GUARD_SEC) > now) set.add(p.mint);
    }
    return set;
  }, [closed]);

  const load = React.useCallback(async () => {
    try {
      const data = await fetchPositions();
      setOpen(data.open || []);
      setClosed(data.recent_closed || []);
      setErr(null);
    } catch (e) {
      setErr(e?.message || "Failed to load positions");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  return (
    <Card className="p-4 sm:p-5" data-testid="positions-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wallet size={15} className="text-[hsl(var(--muted))]" />
          <div>
            <h2 className="text-sm font-semibold">Positions</h2>
            <p className="text-[11px] text-[hsl(var(--muted))]">
              Manual tagging — exits ping <span className="font-mono">@watchlistexits_bot</span>.
              T+5/10/15/60 + −25% backstop, fees-aware.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => setModalOpen(true)}
          data-testid="positions-add-btn"
        >
          <Plus size={13} className="mr-1" /> Mark in position
        </Button>
      </div>

      {err ? (
        <div className="text-xs text-[hsl(var(--danger))] mb-2">{err}</div>
      ) : null}

      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted))]">
              Open · {open.length}
            </h3>
            {loading ? <Loader2 size={11} className="animate-spin text-[hsl(var(--muted))]" /> : null}
          </div>
          {!loading && open.length === 0 ? (
            <div className="text-xs text-[hsl(var(--muted))] py-4 text-center border border-dashed border-[hsl(var(--border))] rounded-md">
              No open positions. Hit “Mark in position” after you buy.
            </div>
          ) : (
            <div className="space-y-1.5">
              <AnimatePresence initial={false}>
                {open.map((p) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.15 }}
                  >
                    <PositionRow pos={p} onClosed={load} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {closed.length > 0 ? (
          <div>
            <h3 className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted))] mb-1.5 flex items-center gap-1">
              <Clock size={10} /> Recently closed · {closed.length}
            </h3>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {closed.map((p) => (
                <ClosedPositionRow
                  key={p.id}
                  pos={p}
                  isGuarded={guardedMints.has(p.mint)}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <AnimatePresence>
        {modalOpen ? (
          <OpenPositionModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            onCreated={() => load()}
          />
        ) : null}
      </AnimatePresence>
    </Card>
  );
}

export default PositionsPanel;
